import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, Empty, Input, Popconfirm, Space, Tag, Typography } from 'antd';
import { useState } from 'react';
import { ScheduleToggle } from '@/components/ScheduleToggle';
import { catalogueApi, type ProductSource } from '../catalogue.api';

const STATUS_COLOR: Record<ProductSource['lastStatus'], string> = {
  never: 'default',
  ok: 'green',
  error: 'red',
};

/**
 * Manage REST sources the catalogue auto-syncs from. Each source is polled
 * (on demand or on a schedule) and its products upserted by externalId/sku.
 */
export function ProductSourcesCard({
  orgId,
  projectId,
  canEdit,
}: {
  orgId: string;
  projectId: string;
  canEdit: boolean;
}) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const key = ['product-sources', orgId, projectId] as const;
  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => catalogueApi.listSources(orgId, projectId),
  });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: key });
  const invalidateProducts = () =>
    queryClient.invalidateQueries({ queryKey: ['products', orgId, projectId] });

  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [token, setToken] = useState('');

  const create = useMutation({
    mutationFn: () =>
      catalogueApi.createSource(orgId, projectId, {
        name,
        url,
        ...(token ? { bearerToken: token } : {}),
      }),
    onSuccess: () => {
      setName('');
      setUrl('');
      setToken('');
      void invalidate();
      message.success('Source added');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to add source'),
  });

  const syncNow = useMutation({
    mutationFn: (id: string) => catalogueApi.syncSource(orgId, projectId, id),
    onSuccess: (res) => {
      void invalidate();
      void invalidateProducts();
      const s = res.summary;
      message.success(s ? `Synced: ${s.created} new, ${s.updated} updated` : 'Synced');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Sync failed'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => catalogueApi.deleteSource(orgId, projectId, id),
    onSuccess: () => {
      void invalidate();
      message.success('Source removed');
    },
  });

  return (
    <Card
      size="small"
      title="Product sources (auto-sync)"
      loading={isLoading}
      extra={
        <ScheduleToggle
          orgId={orgId}
          projectId={projectId}
          kind="sync_catalogue"
          label="Daily auto-sync"
          hint="When on, every enabled source is re-fetched and products upserted daily."
          canEdit={canEdit}
        />
      }
    >
      {canEdit ? (
        <Space wrap style={{ marginBottom: (data?.length ?? 0) > 0 ? 16 : 0 }}>
          <Input
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ width: 140 }}
          />
          <Input
            placeholder="https://store.example.com/products.json"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            style={{ width: 300 }}
          />
          <Input.Password
            placeholder="Bearer token (optional)"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            style={{ width: 200 }}
          />
          <Button
            type="primary"
            onClick={() => create.mutate()}
            loading={create.isPending}
            disabled={!name || !url}
          >
            Add source
          </Button>
        </Space>
      ) : null}

      {(data?.length ?? 0) === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No sources — add a REST endpoint that returns your products as JSON"
        />
      ) : (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          {(data ?? []).map((s) => (
            <Card key={s.id} size="small" type="inner">
              <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
                <Space direction="vertical" size={0}>
                  <Space>
                    <Typography.Text strong>{s.name}</Typography.Text>
                    <Tag color={STATUS_COLOR[s.lastStatus]}>{s.lastStatus}</Tag>
                    {s.hasToken ? <Tag>auth</Tag> : null}
                  </Space>
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: 12, wordBreak: 'break-all' }}
                  >
                    {s.url}
                  </Typography.Text>
                  {s.lastSyncedAt ? (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      Last sync: {new Date(s.lastSyncedAt).toLocaleString()} · {s.lastCount} items
                      {s.lastError ? ` · ${s.lastError}` : ''}
                    </Typography.Text>
                  ) : null}
                </Space>
                {canEdit ? (
                  <Space>
                    <Button
                      size="small"
                      type="primary"
                      loading={syncNow.isPending && syncNow.variables === s.id}
                      onClick={() => syncNow.mutate(s.id)}
                    >
                      Sync now
                    </Button>
                    <Popconfirm title="Remove this source?" onConfirm={() => remove.mutate(s.id)}>
                      <Button size="small" danger>
                        Remove
                      </Button>
                    </Popconfirm>
                  </Space>
                ) : null}
              </Space>
            </Card>
          ))}
        </Space>
      )}
    </Card>
  );
}
